import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ContactPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessEmail: '',
    phone: '',
    industry: '',
    companyName: '',
    message: '',
    consent: false,
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState('idle');
  const [focused, setFocused] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const industries = [
    'Technology', 'Finance', 'Manufacturing', 'Retail',
    'Construction', 'Supply Chain', 'Services', 'Other'
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'Required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Required';
    if (!formData.businessEmail.trim()) {
      newErrors.businessEmail = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)) {
      newErrors.businessEmail = 'Invalid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Required';
    } else if (!/^[\d\s\+\-\(\)]{7,15}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.industry) newErrors.industry = 'Please select an industry';
    if (!formData.companyName.trim()) newErrors.companyName = 'Required';
    if (!formData.consent) newErrors.consent = 'You must agree to continue';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitted('sending');
    try {
      await await axios.post('/api/contact', {
        fullName: formData.firstName + ' ' + formData.lastName,
        company:  formData.companyName,
        email:    formData.businessEmail,
        phone:    formData.phone,
        message:  formData.message || 'Industry: ' + formData.industry,
        services: [formData.industry],
      });
      setSubmitted('success');
    } catch (err) {
      setSubmitted('error');
    }
  };

  const handleClose = () => {
    setSubmitted('idle');
    setFormData({
      firstName: '', lastName: '', businessEmail: '',
      phone: '', industry: '', companyName: '', message: '', consent: false
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=DM+Sans:wght@300;400;500&display=swap');

        :root {
          --gold: #7ec8ec;
          --gold-light: #e8c97a;
          --dark: #0a0a0a;
          --dark-2: #111111;
          --dark-3: #1a1a1a;
          --surface: #161616;
          --border: rgba(201,168,76,0.2);
          --text: #f0ede6;
          --text-muted: #888;
          --error: #e05c5c;
          --radius: 4px;
        }

        .cp-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: overlayIn 0.4s ease;
        }

        @keyframes overlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .cp-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.82);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .cp-modal {
          position: relative;
          background: var(--dark-2);
          border: 1px solid var(--border);
          width: 100%;
          max-width: 560px;
          max-height: 92vh;
          overflow-y: auto;
          border-radius: 2px;
          animation: modalIn 0.45s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,168,76,0.08);
          scrollbar-width: thin;
          scrollbar-color: var(--gold) transparent;
        }

        .cp-modal::-webkit-scrollbar { width: 3px; }
        .cp-modal::-webkit-scrollbar-track { background: transparent; }
        .cp-modal::-webkit-scrollbar-thumb { background: var(--gold); border-radius: 2px; }

        @keyframes modalIn {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .cp-header {
          padding: 36px 36px 24px;
          border-bottom: 1px solid var(--border);
          position: relative;
        }

        .cp-accent-line {
          width: 40px;
          height: 1px;
          background: linear-gradient(90deg, var(--gold), transparent);
          margin-bottom: 16px;
        }

        .cp-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 28px;
          font-weight: 300;
          color: var(--text);
          letter-spacing: 0.02em;
          margin: 0 0 6px;
          line-height: 1.2;
        }

        .cp-title span { color: var(--gold); font-weight: 600; }

        .cp-subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: var(--text-muted);
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin: 0;
        }

        .cp-close {
          position: absolute;
          top: 28px;
          right: 28px;
          background: none;
          border: 1px solid var(--border);
          color: var(--text-muted);
          width: 34px;
          height: 34px;
          border-radius: 2px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .cp-close:hover { border-color: var(--gold); color: var(--gold); background: rgba(201,168,76,0.06); }

        .cp-body { padding: 28px 36px 36px; }

        .cp-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          margin-bottom: 14px;
        }

        .cp-field { display: flex; flex-direction: column; margin-bottom: 14px; }
        .cp-field:last-child { margin-bottom: 0; }

        .cp-label {
          font-family: 'DM Sans', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-bottom: 8px;
          transition: color 0.2s;
        }

        .cp-field.focused .cp-label { color: var(--gold); }

        .cp-input, .cp-select, .cp-textarea {
          background: var(--surface);
          border: 1px solid rgba(255,255,255,0.07);
          color: var(--text);
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 300;
          padding: 13px 16px;
          border-radius: var(--radius);
          outline: none;
          transition: all 0.25s;
          width: 100%;
          box-sizing: border-box;
          -webkit-appearance: none;
        }

        .cp-input::placeholder, .cp-textarea::placeholder { color: rgba(255,255,255,0.2); }
        .cp-select option { background: var(--dark-3); color: var(--text); }

        .cp-input:focus, .cp-select:focus, .cp-textarea:focus {
          border-color: var(--gold);
          background: rgba(22,22,22,0.9);
          box-shadow: 0 0 0 3px rgba(201,168,76,0.08);
        }

        .cp-input.error, .cp-select.error, .cp-textarea.error {
          border-color: var(--error);
          box-shadow: 0 0 0 3px rgba(224,92,92,0.08);
        }

        .cp-select {
          cursor: pointer;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
          padding-right: 40px;
        }

        .cp-textarea { resize: vertical; min-height: 90px; line-height: 1.5; }

        .cp-error-msg {
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          color: var(--error);
          margin-top: 5px;
          letter-spacing: 0.04em;
        }

        .cp-consent {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          margin: 20px 0;
          cursor: pointer;
        }

        .cp-consent-check {
          width: 18px;
          height: 18px;
          min-width: 18px;
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 2px;
          background: var(--surface);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          margin-top: 1px;
          cursor: pointer;
        }

        .cp-consent-check.checked {
          background: var(--gold);
          border-color: var(--gold);
        }

        .cp-consent-check.checked::after {
          content: '✓';
          color: var(--dark);
          font-size: 11px;
          font-weight: 700;
        }

        .cp-consent-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: var(--text-muted);
          line-height: 1.6;
        }

        .cp-consent-text a { color: var(--gold); text-decoration: none; }
        .cp-consent-text a:hover { text-decoration: underline; }

        .cp-submit {
          width: 100%;
          padding: 15px 24px;
          background: linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 50%, var(--gold) 100%);
          background-size: 200% 100%;
          color: var(--dark);
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          border: none;
          border-radius: var(--radius);
          cursor: pointer;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .cp-submit:hover {
          background-position: right center;
          box-shadow: 0 8px 28px rgba(201,168,76,0.3);
          transform: translateY(-1px);
        }

        .cp-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .cp-submit:active { transform: translateY(0); }

        .cp-error-banner {
          background: #1a0a0a;
          border: 1px solid #5c1a1a;
          border-radius: var(--radius);
          padding: 14px 20px;
          color: #e05c5c;
          font-size: 13px;
          font-family: 'DM Sans', sans-serif;
          text-align: center;
          margin-top: 16px;
        }

        .cp-success {
          text-align: center;
          padding: 60px 36px;
          animation: fadeUp 0.5s ease;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .cp-success-icon {
          width: 64px;
          height: 64px;
          border: 1px solid var(--gold);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          font-size: 24px;
          color: var(--gold);
          animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
        }

        @keyframes scaleIn {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .cp-success-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 26px;
          font-weight: 300;
          color: var(--text);
          margin: 0 0 10px;
        }

        .cp-success-msg {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: var(--text-muted);
          line-height: 1.7;
          margin: 0 0 32px;
        }

        .cp-success-btn {
          padding: 12px 32px;
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          border-radius: var(--radius);
          cursor: pointer;
          transition: all 0.2s;
        }
        .cp-success-btn:hover { border-color: var(--gold); color: var(--gold); }

        @media (max-width: 540px) {
          .cp-header, .cp-body { padding-left: 24px; padding-right: 24px; }
          .cp-header { padding-top: 28px; }
          .cp-body { padding-bottom: 28px; }
          .cp-row { grid-template-columns: 1fr; gap: 0; }
          .cp-title { font-size: 24px; }
          .cp-modal { max-height: 96vh; border-radius: 8px 8px 0 0; margin-top: auto; }
          .cp-overlay { align-items: flex-end; padding: 0; }
        }
      `}</style>

      <div className="cp-overlay">
        <div className="cp-backdrop" onClick={handleClose} />

        <div className="cp-modal" role="dialog" aria-modal="true">

          {/* ✅ Error banner — modal ke andar, upar */}
          {submitted === 'error' && (
            <div className="cp-error-banner">
              Something went wrong. Please try again.
            </div>
          )}

          {/* ✅ Success screen */}
          {submitted === 'success' ? (
            <div className="cp-success">
              <div className="cp-success-icon">✓</div>
              <h2 className="cp-success-title">Message Received</h2>
              <p className="cp-success-msg">
                Thank you for reaching out. Our team at InsightWide<br />
                will be in touch with you shortly.
              </p>
              <button className="cp-success-btn" onClick={handleClose}>Close</button>
            </div>
          ) : (
            <>
              <div className="cp-header">
                <div className="cp-accent-line" />
                <h2 className="cp-title">Get in <span>Touch</span></h2>
                <p className="cp-subtitle">InsightWide · Response within 24 hours</p>
                <button className="cp-close" onClick={handleClose} aria-label="Close">✕</button>
              </div>

              <div className="cp-body">
                <form onSubmit={handleSubmit} noValidate>

                  <div className="cp-row">
                    <div className={`cp-field ${focused === 'firstName' ? 'focused' : ''}`}>
                      <label className="cp-label">First Name *</label>
                      <input
                        className={`cp-input ${errors.firstName ? 'error' : ''}`}
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        onFocus={() => setFocused('firstName')}
                        onBlur={() => setFocused('')}
                        placeholder="Jane"
                        autoComplete="given-name"
                      />
                      {errors.firstName && <span className="cp-error-msg">{errors.firstName}</span>}
                    </div>

                    <div className={`cp-field ${focused === 'lastName' ? 'focused' : ''}`}>
                      <label className="cp-label">Last Name *</label>
                      <input
                        className={`cp-input ${errors.lastName ? 'error' : ''}`}
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        onFocus={() => setFocused('lastName')}
                        onBlur={() => setFocused('')}
                        placeholder="Smith"
                        autoComplete="family-name"
                      />
                      {errors.lastName && <span className="cp-error-msg">{errors.lastName}</span>}
                    </div>
                  </div>

                  <div className={`cp-field ${focused === 'businessEmail' ? 'focused' : ''}`}>
                    <label className="cp-label">Business Email *</label>
                    <input
                      className={`cp-input ${errors.businessEmail ? 'error' : ''}`}
                      name="businessEmail"
                      type="email"
                      value={formData.businessEmail}
                      onChange={handleChange}
                      onFocus={() => setFocused('businessEmail')}
                      onBlur={() => setFocused('')}
                      placeholder="jane@company.com"
                      autoComplete="email"
                    />
                    {errors.businessEmail && <span className="cp-error-msg">{errors.businessEmail}</span>}
                  </div>

                  <div className={`cp-field ${focused === 'phone' ? 'focused' : ''}`}>
                    <label className="cp-label">Mobile Phone *</label>
                    <input
                      className={`cp-input ${errors.phone ? 'error' : ''}`}
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocused('phone')}
                      onBlur={() => setFocused('')}
                      placeholder="+61 XXX XXX XXXX"
                      autoComplete="tel"
                    />
                    {errors.phone && <span className="cp-error-msg">{errors.phone}</span>}
                  </div>

                  <div className={`cp-field ${focused === 'industry' ? 'focused' : ''}`}>
                    <label className="cp-label">Industry *</label>
                    <select
                      className={`cp-select ${errors.industry ? 'error' : ''}`}
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      onFocus={() => setFocused('industry')}
                      onBlur={() => setFocused('')}
                    >
                      <option value="">Select your industry</option>
                      {industries.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                    {errors.industry && <span className="cp-error-msg">{errors.industry}</span>}
                  </div>

                  <div className={`cp-field ${focused === 'companyName' ? 'focused' : ''}`}>
                    <label className="cp-label">Company Name *</label>
                    <input
                      className={`cp-input ${errors.companyName ? 'error' : ''}`}
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      onFocus={() => setFocused('companyName')}
                      onBlur={() => setFocused('')}
                      placeholder="InsightWide Pvt Ltd"
                      autoComplete="organization"
                    />
                    {errors.companyName && <span className="cp-error-msg">{errors.companyName}</span>}
                  </div>

                  <div className={`cp-field ${focused === 'message' ? 'focused' : ''}`}>
                    <label className="cp-label">Message</label>
                    <textarea
                      className="cp-textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocused('message')}
                      onBlur={() => setFocused('')}
                      placeholder="Tell us how we can help..."
                    />
                  </div>

                  <div
                    className="cp-consent"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, consent: !prev.consent }));
                      if (errors.consent) setErrors(prev => ({ ...prev, consent: '' }));
                    }}
                  >
                    <div className={`cp-consent-check ${formData.consent ? 'checked' : ''}`} />
                    <p className="cp-consent-text">
                      I consent to InsightWide storing and processing my personal information,
                      as outlined in the <a href="/privacy-policy" onClick={e => e.stopPropagation()}>Privacy Statement</a>.
                    </p>
                  </div>
                  {errors.consent && (
                    <span className="cp-error-msg" style={{ marginTop: '-8px', display: 'block', marginBottom: '12px' }}>
                      {errors.consent}
                    </span>
                  )}

                  <button type="submit" className="cp-submit" disabled={submitted === 'sending'}>
                    {submitted === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>

                </form>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default ContactPopup;